import { Add, ArrowDownward, ArrowUpward, DeleteOutlined, EditOutlined, ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    buildFieldTree,
    type ProductRequirementGroup,
    type ProductSummary,
    type RequirementField,
    type RequirementFieldWithChildren,
} from '../product-requirements-config.dto';

interface Props {
    group: ProductRequirementGroup;
    index: number;
    total: number;
    products: ProductSummary[];
    assignmentsByField: Map<string, string[]>;
    onEditGroup: () => void;
    onDeleteGroup: () => void;
    onMoveGroup: (direction: -1 | 1) => void;
    onAddField: (parentFieldId: string | null) => void;
    onEditField: (field: RequirementField) => void;
    onDeleteField: (field: RequirementField) => void;
    /** New order of a sibling bucket: `parentFieldId = null` → the group's top-level fields. */
    onReorderFields: (parentFieldId: string | null, orderedFieldIds: string[]) => void;
    saving?: boolean;
    /** Non-editors get the catalog view without any mutating controls. */
    readOnly?: boolean;
}

function move<T>(items: T[], from: number, direction: -1 | 1): T[] {
    const to = from + direction;
    if (to < 0 || to >= items.length) return items;
    const next = [...items];
    [next[from], next[to]] = [next[to], next[from]];
    return next;
}

export default function GroupSection(props: Props) {
    const { t } = useTranslation();
    const { group, index, total, products, assignmentsByField } = props;

    const skuById = new Map(products.map((p) => [p.id, p.sku]));
    const roots = buildFieldTree(group.requirementFields);
    const hasFields = group.requirementFields.length > 0;

    function reorderSiblings(siblings: RequirementFieldWithChildren[], from: number, direction: -1 | 1) {
        const reordered = move(siblings, from, direction);
        if (reordered === siblings) return;
        props.onReorderFields(
            siblings[from].parentFieldId ?? null,
            reordered.map((f) => f.id),
        );
    }

    const renderField = (
        field: RequirementFieldWithChildren,
        siblings: RequirementFieldWithChildren[],
        position: number,
        depth: number,
    ) => {
        const assigned = assignmentsByField.get(field.id) ?? [];
        return (
            <Box key={field.id} sx={{ pl: depth * 3 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.5 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2" noWrap>
                                {field.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {field.code}
                            </Typography>
                            <Chip size="small" variant="outlined" label={t(field.fieldType)} />
                            {/* No criticality chip here: the catalog read is product-neutral
                                (GEN-642) — criticality shows per product in the field editor. */}
                            {depth === 0 &&
                                assigned.map((id) => (
                                    <Chip key={id} size="small" color="primary" label={skuById.get(id) ?? id} />
                                ))}
                        </Stack>
                    </Box>
                    {!props.readOnly && (
                        <>
                            <Tooltip title={t('Move up')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={position === 0 || props.saving}
                                        onClick={() => reorderSiblings(siblings, position, -1)}
                                    >
                                        <ArrowUpward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('Move down')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={position === siblings.length - 1 || props.saving}
                                        onClick={() => reorderSiblings(siblings, position, 1)}
                                    >
                                        <ArrowDownward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('Edit')}>
                                <IconButton size="small" onClick={() => props.onEditField(field)}>
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Delete')}>
                                <IconButton size="small" color="error" onClick={() => props.onDeleteField(field)}>
                                    <DeleteOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Stack>

                {field.fieldType === 'list' && (
                    <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 1 }}>
                        {field.subFields.map((child, childIndex) =>
                            renderField(child, field.subFields, childIndex, depth + 1),
                        )}
                        {!props.readOnly && (
                            <Box sx={{ pl: (depth + 1) * 3, py: 0.5 }}>
                                <Button size="small" startIcon={<Add />} onClick={() => props.onAddField(field.id)}>
                                    {t('Add sub-field')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Accordion defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', pr: 1 }}>
                    <Typography sx={{ flexGrow: 1 }}>
                        {group.name}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                            {group.code}
                        </Typography>
                        {/* Placement decides the UI slot (GEN-591) — surface it in the
                            list, not only inside the edit dialog. */}
                        {group.placement === 'service_information' && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color="info"
                                label={t('Services → Information')}
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Typography>
                    {!props.readOnly && (
                        <>
                            <Tooltip title={t('Move up')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={index === 0 || props.saving}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.onMoveGroup(-1);
                                        }}
                                    >
                                        <ArrowUpward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('Move down')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={index === total - 1 || props.saving}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.onMoveGroup(1);
                                        }}
                                    >
                                        <ArrowDownward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('Edit group')}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.onEditGroup();
                                    }}
                                >
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title={hasFields ? t('Remove all fields before deleting the group') : t('Delete group')}
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={hasFields || props.saving}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.onDeleteGroup();
                                        }}
                                    >
                                        <DeleteOutlined fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Divider sx={{ mb: 1 }} />
                {roots.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {t('No fields yet.')}
                    </Typography>
                )}
                {roots.map((root, rootIndex) => renderField(root, roots, rootIndex, 0))}
                {!props.readOnly && (
                    <Box sx={{ mt: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => props.onAddField(null)}
                        >
                            {t('Add field')}
                        </Button>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
